// Model profile: its rank and headline numbers on each side, its matches, its record
// against each opponent, then the breakdown by mode and the technical facts (closed).
// Every prompt version and setup counts.

import { labLogo } from './lab-logos';
import type { LeaderboardResponse, ModelInfo, ModelProfileResponse, PairRow, RoleMetrics, RoleStats } from '../src/api/contract';
import type { Side } from '../src/types';
import { api, describeError, h2hUrl, isNotFound, modelUrl } from './api';
import { matchList } from './cards';
import { chip, emptyState, errorState, fact, getParam, h, link, loadingState, notFoundState, pageHeading, requireEl, show, timeEl, type Child } from './dom';
import { HELP, MIN_RANKED_MATCHES, infoTip, type HelpEntry } from './help';
import { MODE_FILTERS, fmtDateUtc, fmtFixed, fmtInt, fmtPct, fmtRelative, modeLabel, parseDate } from './format';
import { isRanked, metricColumns, rankBadge, ranks } from './metrics';
import { sortableTable, type Column } from './table';

export async function initModel(): Promise<void> {
  const root = requireEl('model-root');
  const id = getParam('id');
  if (!id) {
    show(root, pageHeading('Model profile'), notFoundState('No model selected', 'This link is missing a model id. Pick a model from the leaderboard.'));
    return;
  }

  const load = async (): Promise<void> => {
    show(root, pageHeading('Model profile'), loadingState('Loading model'));
    try {
      // The leaderboards give the model's rank on each side; a failure there only hides the rank.
      const boards = Promise.all(
        (['defender', 'attacker'] as const).map((role) => api.leaderboard({ role, mode: 'all', setup: 'all' }).catch(() => null)),
      );
      const data = await api.model(id);
      if (!data || !data.model || typeof data.model.id !== 'string') throw new Error('The server sent an unexpected response for this model.');
      document.title = `${data.model.displayName || data.model.id} · Model · Avogadro Arena`;
      const [def, atk] = await boards;
      const body = h('div', { class: 'stack' });
      show(root, h('nav', { class: 'crumbs', attrs: { 'aria-label': 'Breadcrumb' } }, link('/', '← Leaderboard')), modelHeader(data.model), body);
      await renderBody(body, data, { defender: def ?? null, attacker: atk ?? null });
    } catch (err) {
      if (isNotFound(err)) {
        show(root, pageHeading('Model profile'), notFoundState('Model not found', 'This model is not in the arena. It may have been delisted before it played.'));
      } else show(root, pageHeading('Model profile'), errorState(describeError(err), () => void load()));
    }
  };
  await load();
}

function modelHeader(m: ModelInfo): HTMLElement {
  const sides = m.attackerEnabled && m.defenderEnabled ? 'Plays both sides' : m.defenderEnabled ? 'Plays defender only' : m.attackerEnabled ? 'Plays attacker only' : 'Not scheduled';
  return h(
    'header',
    { class: 'model-head' },
    h('p', { class: 'eyebrow' }, 'Model', m.maker ? ` · by ${m.maker}` : ''),
    h('h1', { class: 'model-title' }, labLogo(m.maker, 30, { decorative: true }), m.displayName || m.id),
    h('p', { class: 'model-sub' }, sides, m.isActive ? '' : ' · no longer playing'),
  );
}

/** Rank, headline numbers and a link to the matches behind them, for one side. */
function roleCard(role: Side, model: ModelInfo, stats: RoleStats | undefined, board: LeaderboardResponse | null): HTMLElement {
  const side = role === 'attacker' ? 'attacker' : 'defender';
  const enabled = role === 'attacker' ? model.attackerEnabled : model.defenderEnabled;
  const title = role === 'attacker' ? 'As attacker' : 'As defender';
  const titleId = `card-${role}`;
  const matches = stats?.matches ?? 0;
  const parts: Child[] = [h('h2', { attrs: { id: titleId } }, title)];
  if (!stats || matches === 0) {
    parts.push(h('p', { class: 'muted' }, enabled ? `No ${side} matches yet.` : `Doesn't play the ${side}.`));
  } else {
    const rows = (board?.rows ?? []).filter((r) => r && r.matches > 0);
    const ranked = rows.filter(isRanked);
    const me = ranked.find((r) => r.modelId === model.id);
    const rank = me ? (ranks(ranked, role).get(me) ?? null) : null;
    const rankLine: Child[] =
      rank !== null
        ? [rankBadge(rank), h('span', null, ` of ${fmtInt(ranked.length)} ranked ${side}s`)]
        : [h('span', { class: 'muted' }, isRanked(stats) ? 'Rank unavailable right now' : `Not ranked yet: ${fmtInt(matches)} of ${MIN_RANKED_MATCHES} matches needed`)];
    parts.push(h('p', { class: 'role-rank' }, ...rankLine));
    const cols = metricColumns<RoleMetrics>(role, false);
    parts.push(
      h(
        'dl',
        { class: 'role-stats' },
        ...cols.map((c) => h('div', null, h('dt', null, c.label, c.help ? infoTip(c.help) : null), h('dd', null, c.render(stats)))),
      ),
    );
  }
  if (matches > 0) parts.push(h('p', { class: 'role-link' }, link(`/matches?model=${encodeURIComponent(model.id)}&role=${role}`, `Its matches as ${side} →`)));
  return h('section', { class: `role-card role-card--${role === 'attacker' ? 'atk' : 'def'}`, attrs: { 'aria-labelledby': titleId } }, ...parts);
}

function modeTable(role: Side, stats: RoleStats[], model: ModelInfo): HTMLElement | null {
  const rows = MODE_FILTERS.filter((m) => m !== 'all')
    .map((mode) => stats.find((s) => s && s.role === role && s.mode === mode))
    .filter((s): s is RoleStats => s !== undefined && s.matches > 0);
  if (rows.length === 0) return null;
  const columns: Column<RoleStats>[] = [
    { key: 'mode', label: 'Defender mode', sortable: false, value: (r) => r.mode, render: (r) => modeLabel(r.mode) },
    ...metricColumns<RoleStats>(role).map((c) => ({ ...c, sortable: false })),
  ];
  return h(
    'div',
    null,
    h('h3', { class: 'subhead' }, role === 'attacker' ? 'As attacker' : 'As defender'),
    sortableTable({ caption: `${model.displayName || model.id} as ${role}, by defender mode`, columns, rows }),
  );
}

/** One direction of one pairing: the matches behind its row's numbers. */
function pairMatches(model: ModelInfo, opponent: { id: string; name: string }, role: Side): HTMLElement {
  const box = h('div');
  const name = model.displayName || model.id;
  void matchList(box, {
    load: (before) => api.matches({ model: model.id, role, opponent: opponent.id, before, limit: 20 }),
    emptyTitle: 'No matches to show',
    emptyDetail: `${role === 'attacker' ? `${name} hasn't attacked ${opponent.name}` : `${opponent.name} hasn't attacked ${name}`} in a counted match.`,
  });
  return box;
}

const PAIR_STAGES: HelpEntry = {
  term: 'Stages done',
  text: 'Average number of the six stages the attacker got done per match in this pairing. When this model is attacking, higher is better for it; when it is defending, lower is.',
};

function pairSection(model: ModelInfo, pairs: PairRow[]): HTMLElement {
  const id = model.id;
  const rows = pairs.filter((p) => p && (p.mode === 'all' || !p.mode) && (p.modelA === id || p.modelB === id));
  const attacking = (p: PairRow): boolean => p.modelA === id;
  const opponent = (p: PairRow): { id: string; name: string } =>
    attacking(p) ? { id: p.modelB, name: p.modelBName || p.modelB } : { id: p.modelA, name: p.modelAName || p.modelA };
  const columns: Column<PairRow>[] = [
    {
      key: 'opponent',
      label: 'Opponent',
      cls: 'col-model',
      value: (p) => opponent(p).name,
      render: (p) => link(modelUrl(opponent(p).id), opponent(p).name),
    },
    {
      key: 'role',
      label: 'This model was',
      value: (p) => (attacking(p) ? 'attacking' : 'defending'),
      render: (p) => (attacking(p) ? h('span', { class: 'atk' }, 'attacking') : h('span', { class: 'def' }, 'defending')),
    },
    { key: 'matches', label: 'Matches', help: HELP.pairMatches, numeric: true, value: (p) => p.matches, render: (p) => fmtInt(p.matches) },
    {
      key: 'avgStage',
      label: 'Stages done',
      help: PAIR_STAGES,
      numeric: true,
      value: (p) => p.avgStage,
      render: (p) => fmtFixed(p.avgStage, 2),
    },
    { key: 'breachRate', label: 'Breach rate', help: HELP.breachRate, numeric: true, value: (p) => p.breachRate, render: (p) => fmtPct(p.breachRate) },
    {
      key: 'detectionRate',
      label: 'Reported',
      help: { term: 'Reported', text: 'Share of matches in this pairing where the defender reported the attacker to security.' },
      numeric: true,
      value: (p) => p.detectionRate,
      render: (p) => fmtPct(p.detectionRate),
    },
    {
      key: 'last',
      label: 'Last match',
      value: (p) => parseDate(p.lastMatchAt)?.getTime() ?? null,
      numeric: true,
      render: (p) => timeEl(p.lastMatchAt),
    },
    {
      key: 'compare',
      label: 'Compare',
      sortable: false,
      value: () => null,
      render: (p) => {
        const a = link(h2hUrl(id, opponent(p).id), 'head-to-head →');
        a.setAttribute('aria-label', `Head-to-head with ${opponent(p).name}`);
        return a;
      },
    },
  ];
  return h(
    'section',
    { class: 'panel', attrs: { 'aria-labelledby': 'pairs-title' } },
    h('h2', { attrs: { id: 'pairs-title' } }, 'Record against each opponent'),
    h('p', { class: 'hint' }, 'One row per opponent and direction. Open "Matches" on a row to see the games behind it.'),
    rows.length === 0
      ? emptyState('No pairings yet', 'Every attacker meets every defender over time; pairings show up here after their first match.')
      : sortableTable({
          caption: `Pairings involving ${model.displayName || model.id}`,
          columns,
          rows,
          defaultOrder: (a, b) => (parseDate(b.lastMatchAt)?.getTime() ?? 0) - (parseDate(a.lastMatchAt)?.getTime() ?? 0),
          rowHeaderKey: 'opponent',
          detail: {
            column: 'opponent',
            label: 'Matches',
            describe: (p) => `Matches ${attacking(p) ? 'attacking' : 'defending against'} ${opponent(p).name}`,
            render: (p) => pairMatches(model, opponent(p), attacking(p) ? 'attacker' : 'defender'),
          },
        }),
  );
}

function probeView(m: ModelInfo): Child {
  const when = m.probeAt ? h('span', { class: 'muted' }, ` · checked ${fmtRelative(m.probeAt)}`) : null;
  if (m.probeStatus === 'ok') return h('span', null, chip('ok', 'chip--good'), when);
  if (m.probeStatus === 'failed') return h('span', null, chip('failed', 'chip--bad'), m.probeReason ? ` ${m.probeReason}` : '', when);
  return h('span', null, chip('pending', 'chip--dim'), ' not probed yet');
}

function expiryView(expiresAt: string | null): Child {
  const d = parseDate(expiresAt);
  if (!d) return h('span', { class: 'muted' }, 'no end date announced');
  const past = d.getTime() < Date.now();
  const soon = !past && d.getTime() - Date.now() < 10 * 86_400_000;
  return h('span', { class: past ? 'bad' : soon ? 'warn' : '' }, fmtDateUtc(expiresAt), past ? ' (expired)' : ` (${fmtRelative(expiresAt)})`);
}

function techDetails(m: ModelInfo): HTMLElement {
  const status: Child[] = [m.isActive ? chip('active', 'chip--good') : chip('inactive', 'chip--dim')];
  if (m.isListed === false) status.push(' delisted by OpenRouter');
  const cool = parseDate(m.cooldownUntil);
  if (cool && cool.getTime() > Date.now()) status.push(` cooling down until ${fmtRelative(m.cooldownUntil)}`);
  return h(
    'details',
    { class: 'panel stats' },
    h('summary', null, 'Technical details'),
    h(
      'dl',
      { class: 'facts' },
      fact('Model id', h('code', { class: 'model-id' }, m.id)),
      fact('Served by', m.provider || '—'),
      fact('Context', typeof m.contextLength === 'number' ? `${fmtInt(m.contextLength)} tokens` : '—'),
      fact('Max output', typeof m.maxCompletionTokens === 'number' ? `${fmtInt(m.maxCompletionTokens)} tokens` : '—'),
      fact('Probe', probeView(m)),
      fact('Free until', expiryView(m.expiresAt)),
      fact('Status', h('span', null, ...status)),
      fact('Joined', timeEl(m.addedAt, 'absolute')),
    ),
  );
}

async function renderBody(body: HTMLElement, data: ModelProfileResponse, boards: Record<Side, LeaderboardResponse | null>): Promise<void> {
  const stats = Array.isArray(data.stats) ? data.stats : [];
  const all = (role: Side): RoleStats | undefined => stats.find((s) => s && s.role === role && s.mode === 'all');
  const recentBox = h('div');
  const byMode = [modeTable('defender', stats, data.model), modeTable('attacker', stats, data.model)].filter((x): x is HTMLElement => x !== null);
  show(
    body,
    h('div', { class: 'role-cards' }, roleCard('defender', data.model, all('defender'), boards.defender), roleCard('attacker', data.model, all('attacker'), boards.attacker)),
    h(
      'section',
      { attrs: { 'aria-labelledby': 'recent-title' } },
      h('h2', { attrs: { id: 'recent-title' } }, 'Recent matches'),
      recentBox,
    ),
    pairSection(data.model, Array.isArray(data.headToHead) ? data.headToHead : []),
    byMode.length
      ? h(
          'details',
          { class: 'panel stats' },
          h('summary', null, 'Breakdown by defender mode'),
          h('p', { class: 'hint' }, 'The same numbers, split by what the defender was told: naive (ordinary policies), hardened (strict rules), mirror (warned, and told to make the attacker back down).'),
          ...byMode,
        )
      : null,
    techDetails(data.model),
  );
  await matchList(recentBox, {
    initial: (Array.isArray(data.recentMatches) ? data.recentMatches : []).slice(0, 8),
    initialPageSize: 8,
    load: (before) => api.matches({ model: data.model.id, before, limit: 20 }),
    emptyTitle: 'No matches yet',
    emptyDetail: "This model hasn't finished a counted match yet.",
    moreLink: { href: `/matches?model=${encodeURIComponent(data.model.id)}`, text: 'All its matches →' },
  });
}
